// ================================================================
// PROPRIETARY & CONFIDENTIAL — Miskulin Technologies Inc.
// Copyright (c) 2026 Miskulin Technologies. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import { supabase } from './supabase';

const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [onboardingStep, setOnboardingStep] = useState(null);
  const profileLoadId = useRef(0);
  const initializedRef = useRef(false);

  useEffect(() => {
    let resolved = false;
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (resolved) return;
      resolved = true;
      if (session?.user) {
        loadProfile(session.user).then(() => { initializedRef.current = true; });
      } else { setLoading(false); }
    }).catch(() => { if (!resolved) { resolved = true; setLoading(false); } });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'INITIAL_SESSION') {
        if (!resolved && session?.user) { resolved = true; loadProfile(session.user).then(() => { initializedRef.current = true; }); }
        else if (!resolved) { resolved = true; setLoading(false); }
        return;
      }
      if (event === 'SIGNED_OUT') { initializedRef.current = false; setUser(null); setOnboardingStep(null); setLoading(false); return; }
      if (event === 'SIGNED_IN') { if (session?.user) { loadProfile(session.user).then(() => { initializedRef.current = true; }); } return; }
      if (event === 'TOKEN_REFRESHED') { return; }
    });

    const safetyTimeout = setTimeout(() => { if (!resolved) { resolved = true; setLoading(false); } }, 5000);
    return () => { subscription.unsubscribe(); clearTimeout(safetyTimeout); };
  }, []);

  const loadProfile = async (authUser) => {
    const thisLoadId = ++profileLoadId.current;
    try {
      let { data, error } = await supabase.from('users').select('*, company:company_profile(*)').eq('auth_user_id', authUser.id).single();
      if (error || !data) { const res = await supabase.from('users').select('*, company:company_profile(*)').eq('email', authUser.email).single(); data = res.data; }
      let profile = data || { id: authUser.id, email: authUser.email, full_name: authUser.email.split('@')[0] };
      if (!profile.company_id) {
        try { const { data: companyRows } = await supabase.from('company_profile').select('id').limit(1); if (companyRows?.length > 0) { profile = { ...profile, company_id: companyRows[0].id }; } } catch (e) {}
      }
      if (thisLoadId !== profileLoadId.current) return;
      setUser(profile);
      const pwChanged = localStorage.getItem('frcc_password_changed') || profile.password_changed_at || authUser?.user_metadata?.password_changed;
      const ndaSigned = authUser?.user_metadata?.nda_signed_at || profile.nda_signed_at || localStorage.getItem('frcc_nda_signed');
      const tutorialDone = localStorage.getItem('frcc_tutorial_done');
      if (!pwChanged) setOnboardingStep('password');
      else if (!ndaSigned) setOnboardingStep('nda');
      else if (!tutorialDone) setOnboardingStep('tutorial');
      else setOnboardingStep(null);
    } catch (err) {
      if (thisLoadId !== profileLoadId.current) return;
      let fallbackProfile = { id: authUser.id, email: authUser.email, full_name: authUser.email.split('@')[0] };
      try { const { data: companyRows } = await supabase.from('company_profile').select('id').limit(1); if (companyRows?.length > 0) { fallbackProfile = { ...fallbackProfile, company_id: companyRows[0].id }; } } catch (e) {}
      setUser(fallbackProfile);
      const pwChanged = localStorage.getItem('frcc_password_changed') || authUser?.user_metadata?.password_changed;
      const ndaSigned = authUser?.user_metadata?.nda_signed_at || localStorage.getItem('frcc_nda_signed');
      const tutorialDone = localStorage.getItem('frcc_tutorial_done');
      if (!pwChanged) setOnboardingStep('password');
      else if (!ndaSigned) setOnboardingStep('nda');
      else if (!tutorialDone) setOnboardingStep('tutorial');
      else setOnboardingStep(null);
    } finally {
      if (thisLoadId === profileLoadId.current) setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email: email.trim().toLowerCase(), password });
    if (error) throw error;
    return data;
  };

  const signUp = async (email, password, fullName) => {
    const { data, error } = await supabase.auth.signUp({ email, password, options: { data: { full_name: fullName, password_changed: true } } });
    if (error) throw error;
    localStorage.setItem('frcc_password_changed', 'true');
    try { await supabase.from('app_events').insert({ event_type: 'signup', app_name: 'filthyredneckcc', user_email: email, metadata: { full_name: fullName, source: 'web' } }); } catch (e) {}
    return data;
  };

  const completeOnboardingStep = async (step) => {
    if (step === 'password') { localStorage.setItem('frcc_password_changed', 'true'); try { await supabase.auth.updateUser({ data: { password_changed: true } }); } catch(e) {} setOnboardingStep('nda'); }
    else if (step === 'nda') { localStorage.setItem('frcc_nda_signed', 'true'); try { await supabase.auth.updateUser({ data: { nda_signed_at: new Date().toISOString() } }); } catch(e) {} setOnboardingStep('tutorial'); }
    else if (step === 'tutorial') { localStorage.setItem('frcc_tutorial_done', 'true'); setOnboardingStep(null); }
  };

  const signOut = async () => { initializedRef.current = false; await supabase.auth.signOut(); setUser(null); setOnboardingStep(null); };

  return (
    <AuthContext.Provider value={{ user, loading, signIn, signUp, signOut, onboardingStep, completeOnboardingStep }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
